/**
 * The block lists checked at checkout (payments doc 15 §7; R122), on the risk screen.
 *
 * **A value is never shown, only its fingerprint.** The server stores an HMAC of the install id or
 * the gateway's instrument token and returns twelve characters of it — enough to tell two rows
 * apart, not enough to recover the value. A pasted value goes out once, on the add, and is not kept
 * in this screen after the dialog closes.
 *
 * **Confirm fraud fills these by itself**: the devotee's installs and tokenised instruments go on
 * the lists, each row naming the case. Adding by hand is for what the scorer cannot see — a token
 * the gateway flagged, an install support traced.
 *
 * **Removing is not deleting.** The row stays, marked removed with who and why; re-adding the same
 * value re-opens that row rather than starting another.
 */
import { useState } from 'react';
import { StatusChip } from '../components/primitives';
import { Input, Select, Toggle } from '../components/forms';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  useAddRiskBlockEntry,
  useRemoveRiskBlockEntry,
  useRiskBlockList,
  type RiskBlockEntryRow,
  type RiskListKey,
} from '../lib/api/hooks/risk';
import { formatDateTime } from '../lib/datetime';

const LISTS = ['device_install', 'payment_instrument'] as const satisfies readonly RiskListKey[];
const FILTERS = ['all', ...LISTS] as const;
type Filter = (typeof FILTERS)[number];

const label = (value: string) => value.replace(/_/g, ' ');

function AddEntry({ onClose }: { onClose: () => void }) {
  const { toast } = useConsole();
  const add = useAddRiskBlockEntry();
  const [listKey, setListKey] = useState<RiskListKey>('device_install');
  const [value, setValue] = useState('');

  return (
    <ConfirmAction
      open
      title="Put a value on a block list?"
      phrase="BLOCK"
      reason
      blocked={value.trim() === '' ? ['the install id or the gateway token'] : []}
      consequences={[
        'Checkout refuses this install or instrument from the next attempt, for every account that uses it.',
        'Only a fingerprint is kept; the value itself cannot be read back from the list.',
        'Adding a value already listed changes nothing; a removed one goes back on.',
      ]}
      confirmLabel="Add to the list"
      onCancel={onClose}
      onConfirm={(reason) =>
        add.mutate(
          { list_key: listKey, value: value.trim(), reason: reason.trim() },
          {
            onSuccess: (row) => {
              toast({ tone: 'success', message: `${row.fingerprint} on the ${label(row.list_key)} list` });
              onClose();
            },
            onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
          },
        )
      }
    >
      <Select
        label="Which list"
        value={listKey}
        options={LISTS}
        optionLabel={label}
        onChange={(v) => setListKey(v as RiskListKey)}
      />
      <Input label="Value" value={value} onChange={setValue} />
    </ConfirmAction>
  );
}

function RemoveEntry({ entry, onClose }: { entry: RiskBlockEntryRow; onClose: () => void }) {
  const { toast } = useConsole();
  const remove = useRemoveRiskBlockEntry();

  return (
    <ConfirmAction
      open
      title={`Take ${entry.fingerprint} off the ${label(entry.list_key)} list?`}
      phrase={`REMOVE ${entry.fingerprint}`}
      reason
      consequences={[
        'Checkout stops refusing this value from the next attempt.',
        'The entry stays in the history, marked removed with your reason.',
      ]}
      confirmLabel="Take it off"
      onCancel={onClose}
      onConfirm={(reason) =>
        remove.mutate(
          { entryId: entry.id, reason: reason.trim() },
          {
            onSuccess: () => {
              toast({ tone: 'success', message: `${entry.fingerprint} off the ${label(entry.list_key)} list` });
              onClose();
            },
            onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
          },
        )
      }
    />
  );
}

export function BlockLists() {
  const { scope } = useConsole();
  const [filter, setFilter] = useState<Filter>('all');
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const lists = useRiskBlockList(filter, includeRemoved);
  const items = lists.data?.items ?? [];
  /* A removed row opens nothing: there is nothing left to take off. */
  const removing = items.find((e) => e.id === removingId && e.removed_at === null);

  return (
    <Section
      title="Block lists"
      note="checked at checkout — values are kept as fingerprints, never shown"
      actions={
        <>
          <Select
            label="List"
            value={filter}
            options={FILTERS}
            optionLabel={label}
            onChange={(v) => setFilter(v as Filter)}
          />
          <Toggle label="Show removed" on={includeRemoved} onChange={setIncludeRemoved} />
          <ScopeAction scope={scope} need="finance" size="sm" variant="secondary" onClick={() => setAdding(true)}>
            Add to a list
          </ScopeAction>
        </>
      }
      pad={false}
    >
      <div data-testid="block-lists">
        {lists.isPending && <LoadingState message="Reading the block lists…" />}
        {lists.isError && <ErrorState title="Could not read the block lists" cause={errorMessage(lists.error)} />}
        {lists.isSuccess && items.length === 0 && (
          <EmptyState inline icon="shield-check" title="Nothing listed" body="No install or instrument is blocked." />
        )}
        {items.length > 0 && (
          <DataTable
            density="compact"
            onRowClick={(row) => setRemovingId(Number(row.id))}
            columns={[
              { key: 'fingerprint', label: 'Fingerprint', mono: true },
              { key: 'list', label: 'List' },
              { key: 'source', label: 'From' },
              { key: 'added', label: 'Added', mono: true },
              { key: 'state', label: '' },
            ]}
            rows={items.map((e) => ({
              id: String(e.id),
              fingerprint: e.fingerprint,
              list: label(e.list_key),
              source: e.source_case_code ?? 'by hand',
              added: formatDateTime(e.added_at),
              state:
                e.removed_at === null ? (
                  <StatusChip tone="error" label="on the list" />
                ) : (
                  <StatusChip tone="neutral" label="removed" />
                ),
            }))}
          />
        )}
      </div>
      {adding && <AddEntry onClose={() => setAdding(false)} />}
      {removing && <RemoveEntry key={removing.id} entry={removing} onClose={() => setRemovingId(null)} />}
    </Section>
  );
}
